<template>
	<div
		v-if="visibleAlerts.length"
		class="alerts-banner"
		data-test="alerts-banner"
	>
		<TransitionGroup
			name="alert-stack"
			tag="div"
			class="alerts-banner__stack"
			appear
		>
			<div
				v-for="alert in shownAlerts"
				:key="alert.id"
				class="alerts-banner__row"
				:class="`alerts-banner__row--${alert.tone}`"
				:data-test="`alerts-banner-row-${alert.id}`"
			>
				<div class="alerts-banner__icon">
					<Information16 v-if="alert.tone === 'info'" />
					<WarningAlt16 v-else-if="alert.tone === 'amber'" />
					<ErrorFilled16 v-else />
				</div>
				<div class="alerts-banner__content">
					<div class="alerts-banner__title sgnl-text-color-default sgnl-paragraph-s-medium">
						{{ alert.title }}
					</div>
					<div
						v-if="alert.body"
						class="alerts-banner__body sgnl-text-color-secondary sgnl-caption-regular"
					>
						{{ alert.body }}
					</div>
				</div>
				<router-link
					v-if="alert.actionTo"
					:to="alert.actionTo"
					class="alerts-banner__action sgnl-paragraph-s-medium"
					:class="`alerts-banner__action--${alert.tone}`"
					:data-test="`alerts-banner-action-${alert.id}`"
					@click="$posthog?.capture('home_alert_action_clicked', { alert_id: alert.id, tone: alert.tone })"
					>{{ alert.actionLabel }}</router-link
				>
				<v-btn
					icon
					variant="text"
					size="small"
					class="alerts-banner__dismiss"
					aria-label="Dismiss alert"
					:data-test="`alerts-banner-dismiss-${alert.id}`"
					@click="$emit('dismiss', alert.id)"
				>
					<Close16 />
				</v-btn>
			</div>
		</TransitionGroup>

		<div
			v-if="hasOverflow"
			class="alerts-banner__footer"
		>
			<span
				v-if="!expanded"
				class="alerts-banner__summary sgnl-text-color-secondary sgnl-caption-regular"
			>
				<span class="alerts-banner__summary-count sgnl-text-color-secondary"
					>+{{ overflowAlerts.length }} more</span
				>
				<span class="alerts-banner__summary-parts">
					<span
						v-for="part in overflowParts"
						:key="part.tone"
						class="alerts-banner__summary-part"
					>
						<span
							class="alerts-banner__summary-dot"
							:class="`alerts-banner__summary-dot--${part.tone}`"
						/>
						{{ part.label }}
					</span>
				</span>
			</span>
			<v-btn
				variant="text"
				size="small"
				class="alerts-banner__clear"
				@click="$emit('clear-all')"
				>Clear all</v-btn
			>
			<v-btn
				v-if="!expanded"
				variant="text"
				size="small"
				class="alerts-banner__show-all"
				@click="expanded = true"
				>Show all</v-btn
			>
			<v-btn
				v-else
				variant="text"
				size="small"
				class="alerts-banner__collapse"
				@click="expanded = false"
				>Collapse</v-btn
			>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import type { RouteLocationRaw } from "vue-router";

import Close16 from "@carbon/icons-vue/es/close/16";
import Information16 from "@carbon/icons-vue/es/information/16";
import WarningAlt16 from "@carbon/icons-vue/es/warning--alt/16";
import ErrorFilled16 from "@carbon/icons-vue/es/error--filled/16";

export type AlertTone = "info" | "amber" | "red";

export type HomeAlert = {
	id: string;
	tone: AlertTone;
	title: string;
	body?: string;
	actionLabel?: string;
	actionTo?: RouteLocationRaw;
};

const TONE_WEIGHT: Record<AlertTone, number> = { red: 0, amber: 1, info: 2 };
const PREVIEW_LIMIT = 3;

export default defineComponent({
	name: "AlertsBanner",
	components: { Close16, Information16, WarningAlt16, ErrorFilled16 },
	props: {
		alerts: {
			type: Array as PropType<HomeAlert[]>,
			required: true,
		},
	},
	emits: ["dismiss", "clear-all"],
	data: () => ({
		expanded: false,
	}),
	computed: {
		visibleAlerts(): HomeAlert[] {
			return [...this.alerts].sort((a, b) => TONE_WEIGHT[a.tone] - TONE_WEIGHT[b.tone]);
		},
		shownAlerts(): HomeAlert[] {
			if (this.expanded || this.visibleAlerts.length <= PREVIEW_LIMIT) {
				return this.visibleAlerts;
			}
			return this.visibleAlerts.slice(0, PREVIEW_LIMIT);
		},
		overflowAlerts(): HomeAlert[] {
			if (this.expanded || this.visibleAlerts.length <= PREVIEW_LIMIT) {
				return [];
			}
			return this.visibleAlerts.slice(PREVIEW_LIMIT);
		},
		hasOverflow(): boolean {
			return this.visibleAlerts.length > PREVIEW_LIMIT;
		},
		overflowParts(): { tone: AlertTone; label: string }[] {
			const counts: Record<AlertTone, number> = { red: 0, amber: 0, info: 0 };
			this.overflowAlerts.forEach((a) => {
				counts[a.tone] += 1;
			});
			const parts: { tone: AlertTone; label: string }[] = [];
			if (counts.red) parts.push({ tone: "red", label: `${counts.red} critical` });
			if (counts.amber)
				parts.push({ tone: "amber", label: `${counts.amber} warning${counts.amber > 1 ? "s" : ""}` });
			if (counts.info) parts.push({ tone: "info", label: `${counts.info} update${counts.info > 1 ? "s" : ""}` });
			return parts;
		},
	},
});
</script>

<style lang="scss" scoped>
.alerts-banner {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}

.alerts-banner__stack {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}

.alert-stack-enter-active {
	transition:
		transform var(--sgnl-motion-duration-moderate-02) var(--sgnl-expressive-effect-entrance),
		opacity var(--sgnl-motion-duration-moderate-02) var(--sgnl-expressive-effect-entrance);
}
.alert-stack-enter-from {
	opacity: 0;
	transform: translateY(-4px);
}
.alert-stack-enter-active:nth-child(2) {
	transition-delay: 40ms;
}
.alert-stack-enter-active:nth-child(3) {
	transition-delay: 80ms;
}

.alert-stack-leave-active {
	transition: opacity var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-exit);
}
.alert-stack-leave-to {
	opacity: 0;
}
.alert-stack-move {
	transition: transform var(--sgnl-motion-duration-moderate-02) var(--sgnl-expressive-effect-standard);
}

.alerts-banner__row {
	display: grid;
	grid-template-columns: auto 1fr auto auto;
	grid-template-areas: "icon content action dismiss";
	align-items: center;
	gap: var(--sgnl-space-12);
	padding: var(--sgnl-space-12) var(--sgnl-space-16);
	border-radius: var(--sgnl-border-radius-6);
	border: var(--sgnl-border-width-1) solid;
}

.alerts-banner__icon {
	grid-area: icon;
}
.alerts-banner__content {
	grid-area: content;
}
.alerts-banner__action {
	grid-area: action;
}
.alerts-banner__dismiss {
	grid-area: dismiss;
}

@media (max-width: 599px) {
	.alerts-banner__row {
		grid-template-columns: auto 1fr auto;
		grid-template-areas:
			"icon content dismiss"
			"action action action";
		align-items: flex-start;
	}
	.alerts-banner__action {
		justify-self: start;
	}
}

.alerts-banner__row--info {
	background: var(--sgnl-alert-info-background-primary);
	border-color: var(--sgnl-alert-info-border);
}
.alerts-banner__row--amber {
	background: var(--sgnl-alert-warning-background-primary);
	border-color: var(--sgnl-alert-warning-border);
}
.alerts-banner__row--red {
	background: var(--sgnl-alert-error-background-primary);
	border-color: var(--sgnl-alert-error-border);
}

.alerts-banner__icon {
	flex-shrink: 0;
	display: grid;
	place-items: center;
	width: var(--sgnl-icon-20);
	height: var(--sgnl-icon-20);
}
.alerts-banner__row--info .alerts-banner__icon {
	color: var(--sgnl-alert-info-icon);
}
.alerts-banner__row--amber .alerts-banner__icon {
	color: var(--sgnl-alert-warning-icon);
}
.alerts-banner__row--red .alerts-banner__icon {
	color: var(--sgnl-alert-error-icon);
}

.alerts-banner__content {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-2);
	flex: 1;
	min-width: 0;
}

.alerts-banner__action {
	text-decoration: none;
	padding: var(--sgnl-space-6) var(--sgnl-space-10);
	border-radius: var(--sgnl-border-radius-6);
	flex-shrink: 0;
	white-space: nowrap;
}
.alerts-banner__action--info {
	color: var(--sgnl-alert-info-text);
}
.alerts-banner__action--amber {
	color: var(--sgnl-alert-warning-text);
}
.alerts-banner__action--red {
	color: var(--sgnl-alert-error-text);
}

.alerts-banner__dismiss {
	flex-shrink: 0;
	color: var(--sgnl-default-secondary-text);
}

.alerts-banner__footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: var(--sgnl-space-12);
}

.alerts-banner__summary {
	display: inline-flex;
	align-items: center;
	gap: var(--sgnl-space-12);
	margin-right: auto;
	min-width: 0;
}

.alerts-banner__summary-parts {
	display: inline-flex;
	gap: var(--sgnl-space-12);
	align-items: center;
	min-width: 0;
}

.alerts-banner__summary-part {
	display: inline-flex;
	align-items: center;
	gap: var(--sgnl-space-4);
}

.alerts-banner__summary-dot {
	width: var(--sgnl-unit-6);
	height: var(--sgnl-unit-6);
	border-radius: 50%;
}
.alerts-banner__summary-dot--info {
	background: var(--sgnl-alert-info-icon);
}
.alerts-banner__summary-dot--amber {
	background: var(--sgnl-alert-warning-icon);
}
.alerts-banner__summary-dot--red {
	background: var(--sgnl-alert-error-icon);
}

.alerts-banner__clear,
.alerts-banner__show-all,
.alerts-banner__collapse {
	flex-shrink: 0;
}
</style>
